import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import StakeholderFeedbackSystem from '../feedback/StakeholderFeedbackSystem'
import FeedbackRequestDialog from '../feedback/FeedbackRequestDialog'
import { feedbackService, type FeedbackRequest } from '@/services/api/feedbackService'

interface ReviewFeedbackViewProps {
  userRole: 'manager' | 'employee'
  memberId: string
  memberFeedbackRequests: FeedbackRequest[]
  loadingFeedback: boolean
  /**
   * Optional parent handler implementing the dual-purpose onCreateRequest
   * contract (bare `{type}` opens a dialog; full payload submits). When the
   * parent doesn't supply one — e.g. the employee "My reflection" workspace
   * rendered from ReviewsTab — this view handles both cases itself. The old
   * `|| console.log` fallback silently swallowed the employee's "Request
   * Feedback" click, which presented as "the dialog doesn't open".
   */
  onCreateFeedbackRequest?: (request: any) => void | Promise<any>
  onRefreshFeedback: () => void
  canRate: boolean
  isReviewCompleted: boolean
  isFormalReview: boolean
  allKPAsRated: boolean
  setViewMode: (mode: 'kpa' | 'feedback' | 'finalize') => void
}

export function ReviewFeedbackView({
  userRole,
  memberId,
  memberFeedbackRequests,
  loadingFeedback,
  onCreateFeedbackRequest,
  onRefreshFeedback,
  canRate,
  isReviewCompleted,
  isFormalReview,
  allKPAsRated,
  setViewMode,
}: ReviewFeedbackViewProps) {
  // Local fallback dialog state — only used when no parent handler exists.
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false)
  const [feedbackRequestType, setFeedbackRequestType] =
    useState<'MATRIX' | 'EMPLOYEE_REQUESTED' | 'UPWARD_360' | 'INFORMATION_CHECKIN'>('EMPLOYEE_REQUESTED')

  const handleCreateRequest = async (request: any) => {
    // Parent-owned flow (manager journey passes a real handler) — defer.
    if (onCreateFeedbackRequest) {
      return onCreateFeedbackRequest(request)
    }

    // Self-contained flow (mirrors FeedbackTab.handleCreateRequest):
    if (request.stakeholders && request.custom_questions) {
      // Full submission from StakeholderFeedbackSystem's internal dialog
      try {
        const result = await feedbackService.createFeedbackRequest(request)
        onRefreshFeedback()
        return result
      } catch (error: any) {
        const detail = error.response?.data?.detail
        const message = Array.isArray(detail)
          ? detail.map((e: any) => e.msg || e.message || String(e)).join(', ')
          : (typeof detail === 'string' ? detail : 'Failed to send feedback request')
        toast.error(message)
        throw error
      }
    }

    // Bare dialog trigger ("Request Feedback" button)
    setFeedbackRequestType(request.type === 'employee-requested' ? 'EMPLOYEE_REQUESTED' : 'MATRIX')
    setShowFeedbackDialog(true)
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <StakeholderFeedbackSystem
          userRole={userRole}
          requests={memberFeedbackRequests}
          userId={memberId}
          loading={loadingFeedback}
          onCreateRequest={handleCreateRequest}
          onRefresh={onRefreshFeedback}
        />
      </div>

      {/* Local Feedback Request Dialog (no-parent-handler fallback) */}
      {showFeedbackDialog && (
        <FeedbackRequestDialog
          open={showFeedbackDialog}
          onOpenChange={setShowFeedbackDialog}
          requestType={feedbackRequestType}
          currentUserId={memberId}
          onSubmit={async (data) => {
            try {
              const enrichedData = {
                ...data,
                requested_by_user_id: memberId,
                is_anonymous: data.is_confidential || false,
                integrate_to_evidence: true,
                request_type: data.request_type.toLowerCase().replace('-', '_'),
              }
              await feedbackService.createFeedbackRequest(enrichedData as any)
              setShowFeedbackDialog(false)
              onRefreshFeedback()
            } catch (error: any) {
              console.error('Failed to create feedback request:', error)
              toast.error(error.response?.data?.detail || 'Failed to send feedback request')
            }
          }}
        />
      )}

      {/* Feedback Footer Navigation */}
      <div className="border-t bg-gray-50 p-4 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setViewMode('kpa')}
        >
          Back to KPA Check-ins
        </Button>

        {(canRate || isReviewCompleted) && (
          <Button
            onClick={() => setViewMode('finalize')}
            className="bg-indigo-600 hover:bg-indigo-700"
            disabled={isFormalReview && !allKPAsRated}
          >
            <Lock className="h-4 w-4 mr-2" />
            {isFormalReview && !allKPAsRated ? 'Rate All KPAs First' : 'Go to Finalise'}
          </Button>
        )}
      </div>
    </div>
  )
}
